import {
    Timeout,
    decodeEntities, generateBackgroundImgUrl, setStylesOnElement
} from "../../util/Misc";
import { SYSTEM_STATE } from "../../model/Constants";
import SndMgr from "../../util/SndMgr";
import Aria from "../../util/Aria";
import {systemRender} from "../templates/renders/system";
import { customOutOfFocus } from "../../controller/TranscriptController";

/**
 * Creates system view custom element that is displayed for every system message
 * @type {{template, reload(*), created(): void, tagName: string, render(*): void}}
 */
const SystemView: any = {
    tagName: "system-view",
    initialize(options) {
        initComponent(options);
    },
    renderer: systemRender,
    created() {
        this.ariaUtil = new Aria(this.$app);
        this.cont = this.el.firstChild;
        this.system = this.cont.querySelector(".system");
        setStylesOnElement(this.systemStyleProps, this.cont);
    },
    reload(text) { // eslint-disable-line  no-unused-vars

    },

    /**
     * Renders the system message text and styles depends upon the system message state
     * @param {Message} message
     */
    render(message) {
        const msgText = message.getMessageText();
        const cl = this.system.classList;
        const imgel = this.system.firstElementChild;
        let cStyles;
        const systemState = message.getInternalState();
        this.data.messageText = decodeEntities(msgText);
        customOutOfFocus(this.system, this.$app);

        switch (systemState) {
            case SYSTEM_STATE.QUEUE_MSG:
                cStyles = this.styleQ;
                cl.add("system-queue");
                break;

            case SYSTEM_STATE.EXIT_MSG:
                cStyles = this.styleE;

                if (this.sepHeaderE) {
                    cl.add("seperator");
                }

                cl.add("system-exit");
                break;

            case SYSTEM_STATE.DENIED_MSG:
                cStyles = this.styleD;
                cl.add("system-denied");
                break;

            case SYSTEM_STATE.LOST:
                cStyles = this.styleL;
                cl.add("system-lost");
                break;

            case SYSTEM_STATE.TRANSFER_STARTED:
                cStyles = this.styleTI;
                cl.add("system-transfer-init");
                break;

            case SYSTEM_STATE.TRANSFER_JOINED:
                cStyles = this.styleTJ;

                if (this.sepHeaderTJ) {
                    cl.add("seperator");
                }

                cl.add("system-transfer-joined");
                break;

            case SYSTEM_STATE.CONFERENCE:
                cStyles = this.styleC;

                if (this.sepHeaderC) {
                    cl.add("seperator");
                }

                cl.add("system-conference");
                break;

            case SYSTEM_STATE.NETWORK_DISCON:

            case SYSTEM_STATE.NETWORK_RECON:

            case SYSTEM_STATE.NETWORK_FAILED:
                cStyles = this.styleN;
                cl.add("system-network");
                break;

            case SYSTEM_STATE.LOADING_MSG:
                cStyles = this.styleL;
                cl.add("system-loading");
                this.el.classList.add("loading-view");
                break;

            default:
                cStyles = this.style;
                cl.add("system");
        }

        this.setSystemImage(systemState, imgel);

        if (cStyles) {
            setStylesOnElement(cStyles, this.system);
        }

        let announce = false;

        if (this.$app.getRestoring()) {
            this.show();
            announce = message.isLast();
        } else {
            announce = true;

            if (Timeout.exists("animatetimer") && !Timeout.executed("animatetimer")) {
                Timeout.clear("animatetimer")();
            }

            Timeout.set("animatetimer", () => {
                this.show();
            }, 25);
        }

        if (announce) {
            this.ariaUtil.announceBubbleText(msgText, message.getName());
        }

        this.playSoundForSystemStates(systemState);
    },

    playSoundForSystemStates(systemState) {

        // Fetching the Sound Manager Instance for respective System State
        const systemStateSndMgr = this.sndMgrMap.get(systemState);
        setTimeout(() => {
            if (systemStateSndMgr) {
                systemStateSndMgr.playSound();
            }
        }, 500);
    },

    /**
     * @param {HTMLElement} img - div which has image background
     * @param state
     */
    setSystemImage(state, img) {
        if (this.systemStateConfigs && this.systemStateConfigs[state]) {
            const url = generateBackgroundImgUrl(this.systemStateConfigs[state], this.$app);
            img.style.display = null;

            if (url) {
                img.style.backgroundImage = url; // eslint-disable-line no-param-reassign
                img.classList.add("enter"); // eslint-disable-line no-param-reassign
            } else {
                img.style.display = "none"; // eslint-disable-line no-param-reassign
            }
        } else {
            img.style.display = "none"; // eslint-disable-line no-param-reassign
        }
    },
    hide() {
        this.system.classList.remove("enter");
    },
    show() {
        this.system.classList.add("enter");
    },
};

const createSndMgrMapFromSystemStates = (systemStateConfigs, sndMgrMap, appInstance) => {
    for (let [key, value] of Object.entries(systemStateConfigs)) {
        if(value["audioSource"]) {
            sndMgrMap.set(key, new SndMgr(appInstance, value["audioSource"]))
        }
    }
};

/**
 * @export SystemView
 * @param {CIApp} appInstance
 * @return {{template, reload, (*), created, (): void, tagName: string, render, (*): void}}
 */
export default appInstance => {
    SystemView.$app = appInstance;
    const constObj: any = {};
    SystemView.constants = constObj;

    // IE will break if messageText is not predefined, throws undefined Error
    SystemView.data = { messageText: "" };
    return SystemView;
};

const initComponent = (options: any) => {
    const appInstance = options.$app;
    const constObj: any = options.constants;
    const config = appInstance.getUIConfig();

    const styleConfig: any = {};
    let systemStyleProps;

    if (config.transcriptConfig) {
        const tConfig = config.transcriptConfig;
        constObj.style = tConfig.systemViewStyles;
        constObj.styleQ = tConfig.systemViewQueueStyles;
        constObj.styleE = tConfig.systemViewExitStyles;
        constObj.styleD = tConfig.systemViewDeniedStyles;
        constObj.styleL = tConfig.systemViewLostStyles;
        constObj.styleTI = tConfig.systemViewTransferStartedStyles;
        constObj.styleTJ = tConfig.systemViewTransferJoinedStyles;
        constObj.styleC = tConfig.systemViewConferenceStyles;
        constObj.styleN = tConfig.systemViewNetworkStyles;
        constObj.styleL = tConfig.systemViewLoadingStyles;
        constObj.iconstyle = tConfig.systemIconStyles;
        constObj.sepHeaderTJ = config.appConfigProps && config.appConfigProps.showSeperatorTransfer;
        constObj.sepHeaderE = config.appConfigProps && config.appConfigProps.showSeperatorExit;
        constObj.sepHeaderC = config.appConfigProps && config.appConfigProps.showSeperatorConference;
        constObj.systemStateConfigs = tConfig.systemConfig || tConfig.systemImageConfig;
        systemStyleProps = config.transcriptConfig.systemStyleProperties || {};
    } else {
        systemStyleProps = {};
    }

    constObj.sndMgrMap = new Map();

    /*
     *Creating a Sound Manager Instance for every systemState Config which have an audioSource
     *Multiple instances are created because Sound Manager have single instance of audioSource
     *and here we need unique instance for each System State
     */
    if (constObj.systemStateConfigs) {
        createSndMgrMapFromSystemStates(constObj.systemStateConfigs, constObj.sndMgrMap, appInstance);
    }

    styleConfig.marginLeft = `${systemStyleProps.systemLeftMargin || 0}px`;
    styleConfig.marginBottom = `${systemStyleProps.systemBottomMargin || 15}px`;
    styleConfig.marginRight = `${systemStyleProps.systemRightMargin || 5}px`;
    styleConfig.marginTop = `${systemStyleProps.systemTopMargin || 0}px`;

    constObj.systemStyleProps = styleConfig;
};
